import { GenericItemsForm, GenericItemsUtils } from "@leap4/frontend";
import { baseBackendRoute } from "../../../config/generals";
import { VerifiedUser, CheckCircle, Cancel, Email } from "@mui/icons-material";
import React, { useState } from "react";
import { IconButton, Tooltip, CircularProgress, Chip } from "@mui/material";
import { useDispatch } from "react-redux";
import { resendVerificationEmail } from "../../../actions/users";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";


const fields: any = { name: 1, isActive: 1, email: 1, updated: 1, createdAt: 1, external: 1, emailVerified: 1, lastLoginAt: 1 }
const columnsFields: any = { name: 1, isActive: 1, email: 1, updated: 1, createdAt: 1, external: 1 }

const {
    store,
    columns: baseColumns
} = GenericItemsUtils.GenerateDefaultStoreAndColumns({ baseUrl: `${baseBackendRoute}/users/filter/internal` }, columnsFields, {
    isActive: {},
    created: {},
    name: { editLinkFunc: (id) => `/users/${id}` },
    updated: {}
})

const InternalUsersManagementPanel = () => {
    const dispatch = useDispatch();
    const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

    const handleResendEmail = async (userId: string) => {
        setLoadingStates(prev => ({ ...prev, [userId]: true }));
        await dispatch(resendVerificationEmail(userId) as any);
        setLoadingStates(prev => ({ ...prev, [userId]: false }));
    };

    const lastLoginAtColumn: GridColDef = {
        field: 'lastLoginAt',
        headerName: 'Last Login',
        width: 180,
        renderCell: (params: GridRenderCellParams) => {
            const value = params.value;
            if (!value) return <span style={{ color: '#999' }}>Never</span>;

            // Handle both old format (Date) and new format ({ date, serviceName })
            const dateValue = value.date || value;
            const serviceName = value.serviceName;

            const date = new Date(dateValue);
            const formattedDate = date.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).replace(',', '');

            if (serviceName) {
                return (
                    <Tooltip title={`Service: ${serviceName}`}>
                        <span>{formattedDate}</span>
                    </Tooltip>
                );
            }
            return formattedDate;
        }
    };

    const emailVerifiedColumn: GridColDef = {
        field: 'emailVerified',
        headerName: 'Email Verified',
        width: 140,
        renderCell: (params: GridRenderCellParams) => {
            const isVerified = params.value;
            return (
                <Tooltip title={isVerified ? "Email verified" : "Email not verified"}>
                    {isVerified ? (
                        <Chip
                            icon={<CheckCircle />}
                            label="Verified"
                            color="success"
                            size="small"
                        />
                    ) : (
                        <Chip
                            icon={<Cancel />}
                            label="Not Verified"
                            color="error"
                            size="small"
                        />
                    )}
                </Tooltip>
            );
        }
    };

    const actionsColumn: GridColDef = {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
            const isVerified = params.row.emailVerified;
            const userId = params.row._id;
            const isLoading = loadingStates[userId];

            if (isVerified) return null;

            return (
                <Tooltip title="Resend verification email">
                    <IconButton
                        color="primary"
                        onClick={() => handleResendEmail(userId)}
                        disabled={isLoading}
                        size="small"
                    >
                        {isLoading ? <CircularProgress size={20} /> : <Email />}
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const columns = [...baseColumns, lastLoginAtColumn, emailVerifiedColumn, actionsColumn];

    return <GenericItemsForm
        store={store}
        columns={columns}
        getAllParams={fields}
        breadcrumbs={{
            link: "/internalUsers",
            text: "Internal users",
            icon: <VerifiedUser />
        }}
        createButton={{ to: "/users/add", text: "Create User" }}
        tableProps={{ pageSize: 50 }}
    />
}

export { InternalUsersManagementPanel };